
// TrendsPage.tsx
import React, { useState, useMemo, useContext, useEffect } from "react";
import styles from "./TrendsPage.module.css";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { Globe, SearchIcon, Smartphone, Calendar, X } from "lucide-react";
import { useTrendsData, getDateRangeForPeriod } from "./hooks/useTrendsData";
import {
  getTrendsMetricLabel,
  getTrendsPeriodButtonTitle,
  getTrendsPeriodDescription,
} from "./utils/trendsPeriodLabels";
import type { LeagueSalesData, MarketplaceData, TrendsChartDataPoint } from "./api/TrendsPageQuery";
import { LineChart, Line, AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { globalContext } from '../../Provider/AppProvider';

// Define types for KPI values
interface KPIValues {
  totalRevenue: number;
  qaProfit: number; 
  sold: number;
  avgPrice: number;
  revenueChange: string;
  profitChange: string;
  soldChange: string;
  priceChange: string;
}


const TrendsPage = () => {
  const [viewMode, setViewMode] = useState<"web" | "mobile">("web");
  const [selectedDateRange, setSelectedDateRange] = useState("90D");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([
    "Total Revenue",
    "QA Profit",
    "Sold",
    "Avg Price"
  ]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const dateRanges = ["7D", "30D", "90D", "YTD", "All", "Custom"];
  
  // Custom date range state
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Get userId and userRole from context
  const { globalState } = useContext(globalContext);
  const userId = globalState?.profile?.id;
  const userRole = globalState?.profile?.userRole;
  const userEmail = (
    globalState?.profile?.mailId ||
    localStorage.getItem("adminMailId") ||
    ""
  ).toLowerCase().trim();

  const dateRangeParams = useMemo(() => {
    if (selectedDateRange === 'Custom' && customStartDate && customEndDate) {
      console.group('[TrendsPage] 📅 Custom Date Range Selected');
      console.log('Start Date:', customStartDate);
      console.log('End Date:', customEndDate);
      console.groupEnd();
      return { startDate: customStartDate, endDate: customEndDate };
    }
    const params = getDateRangeForPeriod(selectedDateRange);
    console.log('[TrendsPage] 📆 Date Range:', { range: selectedDateRange, ...params });
    return params;
  }, [selectedDateRange, customStartDate, customEndDate]);

  const periodDescription = useMemo(
    () => getTrendsPeriodDescription(selectedDateRange, dateRangeParams.startDate, dateRangeParams.endDate),
    [selectedDateRange, dateRangeParams.startDate, dateRangeParams.endDate],
  );

  const getDatesForRangeButton = (range: string) => {
    if (range === 'Custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    return getDateRangeForPeriod(range);
  };

  // Helper to parse MM/DD date strings to Date objects (current year)
  const parseDate = (dateStr: string): Date => {
    const now = new Date();
    const [month, day] = dateStr.split('/').map(Number);
    const date = new Date(now.getFullYear(), month - 1, day);
    // If the date is in the future, assume it's from last year
    if (date > now) date.setFullYear(now.getFullYear() - 1);
    return date;
  };
  const {
    chartData: apiChartData,
    leagueData,
    marketplaceData,
    leagueSeries,
    marketplaceSeries,
    organizationStats,
    revenueAggregate,
    revenueLoading,
    allTimeSoldCount,
    loading,
    refetch,
  } = useTrendsData({
    startDate: dateRangeParams.startDate,
    endDate: dateRangeParams.endDate,
    period: selectedDateRange,
    userId,
    userRole,
    userEmail,
  });

  // Log backend API calls
  useEffect(() => {
    console.group('[TrendsPage] 🌐 Backend API Called');
    console.log('API Parameters:', {
      startDate: dateRangeParams.startDate,
      endDate: dateRangeParams.endDate,
      userId,
      userRole,
      dateRange: selectedDateRange
    });
    console.log('Loading:', loading);
    console.log('API Response Data Points:', apiChartData?.length || 0);
    console.groupEnd();
  }, [dateRangeParams.startDate, dateRangeParams.endDate, userId, userRole, selectedDateRange, loading, apiChartData?.length]);

  const fullChartData = apiChartData ?? [];
  
  const categories = [
    "All",
    "Financial",
    "Ticket Stats",
    "Status Breakdown",
    "Potential Payout",
    "Organization",
    "League Sales",
    "Marketplace Sales"
  ];

  const metricSections = useMemo(() => {
    return [
      {
        title: "Financial",
        metrics: ["Total Revenue", "QA Profit", "User Payouts", "VL Fee"],
      },
      {
        title: "Ticket Stats",
        metrics: ["Pending", "Listed", "Sold", "Settlement", "Avg Price"],
      },
      {
        title: "Status Breakdown",
        metrics: ["To Be Verified", "Verified", "Listed", "Sold", "Delisted", "Unsold"],
      },
      {
        title: "Potential Payout",
        metrics: ["Total", "Self Pricing", "Concierge"],
      },
      {
        title: "Organization",
        metrics: ["Total Users", "Active Users", "Avg Rev/User", "Delist Rate"],
      },
      {
        title: "League Sales",
        metrics: ["NBA", "NFL", "MLB", "NHL", "MLS", "NCAA FB", "NCAA BB", "Concerts", "Theater"],
      },
      {
        title: "Marketplace Sales",
        metrics: ["StubHub", "SeatGeek", "Ticketmaster", "Vivid Seats", "TickPick", "Gametime"],
      },
    ];
  }, []);

  const visibleMetricSections = useMemo(() => {
    if (selectedCategory === "All") {
      return metricSections;
    }

    if (selectedCategory === "Organization") {
      return metricSections.filter((section) =>
        ["Organization", "League Sales", "Marketplace Sales"].includes(section.title)
      );
    }

    return metricSections.filter((section) => section.title === selectedCategory);
  }, [metricSections, selectedCategory]);

  const allowedMetrics = useMemo(() => {
    return new Set(visibleMetricSections.flatMap((section) => section.metrics));
  }, [visibleMetricSections]);

  const activeSelectedMetrics = useMemo(() => {
    return selectedMetrics.filter((metric) => allowedMetrics.has(metric));
  }, [allowedMetrics, selectedMetrics]);

  const chartData = useMemo((): TrendsChartDataPoint[] => {
    const now = new Date();
    let data = [...fullChartData];
    let startDate: Date | null = null;
    let endDate: Date | null = null;

   

    // Filter by date range
    if (["7D", "30D", "90D", "YTD", "Custom"].includes(selectedDateRange)) {
      if (selectedDateRange === "7D") {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 6);
      } else if (selectedDateRange === "30D") {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 29);
      } else if (selectedDateRange === "90D") {
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 89);
      } else if (selectedDateRange === "YTD") {
        startDate = new Date(now.getFullYear(), 0, 1);
      } else if (selectedDateRange === "Custom" && dateRangeParams.startDate && dateRangeParams.endDate) {
        startDate = new Date(dateRangeParams.startDate);
        endDate = new Date(dateRangeParams.endDate);
        console.log('🎯 Custom range filtering:', {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate?.toISOString().split('T')[0]
        });
      }

      if (startDate) {
        if (!endDate) endDate = now;
        data = data.filter((item: TrendsChartDataPoint) => {
          const itemDate = parseDate(item.date);
          const isInRange = itemDate >= startDate! && itemDate <= endDate!;
          return isInRange;
        });
      }
    }

    console.log('After date filtering:', data.length, 'points');

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter((item: TrendsChartDataPoint) => item.date.toLowerCase().includes(query));
      console.log('After search filtering:', data.length, 'points');
    }

    // Always sort by date ascending
    data = [...data].sort((a, b) => parseDate(a.date).getTime() - parseDate(b.date).getTime());
    console.log('Final sorted data:', data.length, 'points');
    console.log('Date range in data:', data.length > 0 ? `${data[0].date} to ${data[data.length - 1].date}` : 'No data');
    console.groupEnd();

    return data;
  }, [fullChartData, searchQuery, selectedDateRange, dateRangeParams.startDate, dateRangeParams.endDate]);

  useEffect(() => {
    if (selectedCategory === "All") {
      return;
    }

    setSelectedMetrics((currentMetrics) => {
      const filtered = currentMetrics.filter((metric) => allowedMetrics.has(metric));
      if (filtered.length > 0) {
        return filtered;
      }

      const defaults = visibleMetricSections[0]?.metrics.slice(0, 4) ?? [];
      return defaults;
    });
  }, [allowedMetrics, selectedCategory, visibleMetricSections]);

  // Calculate KPI values based on filtered data and category selection
  const kpiValues = useMemo((): KPIValues => {
    const dataToUse = chartData;
    
    console.group('[TrendsPage] 📊 KPI Values Calculation');
    console.log('Data points for KPI:', dataToUse.length);
    console.log('Category:', selectedCategory);
    
    if (dataToUse.length === 0) {
      console.log('⚠️ No data available for KPI calculation');
      console.groupEnd();
      return {
        totalRevenue: 0,
        qaProfit: 0,
        sold: 0,
        avgPrice: 0,
        revenueChange: "0",
        profitChange: "0",
        soldChange: "0",
        priceChange: "0"
      };
    }

    const latestData = dataToUse[dataToUse.length - 1];
    const firstData = dataToUse[0];
    console.log('Date range in data:', { first: firstData.date, latest: latestData.date });

    // Apply category-based filtering to KPI calculations
    let totalRevenue = latestData.revenue;
    let qaProfit = latestData.qaProfit;
    let sold = latestData.sold;
    let avgPrice = latestData.price;

    // Filter KPI values based on selected category
    if (selectedCategory === "Ticket Stats") {
      totalRevenue = latestData.sold * latestData.price;
      qaProfit = latestData.profit;
      sold = latestData.sold;
      avgPrice = latestData.price;
    } else if (selectedCategory === "Financial") {
      totalRevenue = latestData.revenue;
      qaProfit = latestData.qaProfit;
      sold = latestData.sold;
      avgPrice = latestData.price;
    } else if (selectedCategory === "Status Breakdown") {
      totalRevenue = latestData.listed + latestData.sold + latestData.pending;
      qaProfit = latestData.verified;
      sold = latestData.sold;
      avgPrice = latestData.toBeVerified;
    } else if (selectedCategory === "Organization") {
      totalRevenue = latestData.listed;
      qaProfit = latestData.verified;
      sold = latestData.sold;
      avgPrice = latestData.pending;
    }

    // Calculate percentage changes with proper error handling
    let revenueChange: string;
    let profitChange: string;
    let soldChange: string;
    let priceChange: string;

    if (selectedCategory === "Ticket Stats") {
      revenueChange = firstData.sold && firstData.price ? (((latestData.sold * latestData.price) - (firstData.sold * firstData.price)) / (firstData.sold * firstData.price) * 100).toFixed(1) : "0";
      profitChange = firstData.profit ? ((latestData.profit - firstData.profit) / firstData.profit * 100).toFixed(1) : "0";
      soldChange = firstData.sold ? ((latestData.sold - firstData.sold) / firstData.sold * 100).toFixed(1) : "0";
      priceChange = firstData.price ? ((latestData.price - firstData.price) / firstData.price * 100).toFixed(1) : "0";
    } else if (selectedCategory === "Status Breakdown") {
      revenueChange = firstData.listed ? (((latestData.listed + latestData.sold + latestData.pending) - (firstData.listed + firstData.sold + firstData.pending)) / (firstData.listed + firstData.sold + firstData.pending) * 100).toFixed(1) : "0";
      profitChange = firstData.verified ? ((latestData.verified - firstData.verified) / firstData.verified * 100).toFixed(1) : "0";
      soldChange = firstData.sold ? ((latestData.sold - firstData.sold) / firstData.sold * 100).toFixed(1) : "0";
      priceChange = firstData.toBeVerified ? ((latestData.toBeVerified - firstData.toBeVerified) / firstData.toBeVerified * 100).toFixed(1) : "0";
    } else if (selectedCategory === "Organization") {
      revenueChange = firstData.listed ? ((latestData.listed - firstData.listed) / firstData.listed * 100).toFixed(1) : "0";
      profitChange = firstData.verified ? ((latestData.verified - firstData.verified) / firstData.verified * 100).toFixed(1) : "0";
      soldChange = firstData.sold ? ((latestData.sold - firstData.sold) / firstData.sold * 100).toFixed(1) : "0";
      priceChange = firstData.pending ? ((latestData.pending - firstData.pending) / firstData.pending * 100).toFixed(1) : "0";
    } else {
      revenueChange = firstData.revenue ? ((latestData.revenue - firstData.revenue) / firstData.revenue * 100).toFixed(1) : "0";
      profitChange = firstData.qaProfit ? ((latestData.qaProfit - firstData.qaProfit) / firstData.qaProfit * 100).toFixed(1) : "0";
      soldChange = firstData.sold ? ((latestData.sold - firstData.sold) / firstData.sold * 100).toFixed(1) : "0";
      priceChange = firstData.price ? ((latestData.price - firstData.price) / firstData.price * 100).toFixed(1) : "0";
    }

    console.log('✅ KPI Values Calculated:', {
      totalRevenue,
      qaProfit,
      sold,
      avgPrice,
      changes: { revenueChange, profitChange, soldChange, priceChange }
    });
    console.groupEnd();

    return {
      totalRevenue,
      qaProfit,
      sold,
      avgPrice,
      revenueChange,
      profitChange,
      soldChange,
      priceChange
    };
  }, [chartData, selectedCategory]);

  const combinedChartData = useMemo(() => {
    const baseSeries: Record<string, keyof TrendsChartDataPoint> = {
      "Total Revenue": "revenue",
      "QA Profit": "profit",
      "User Payouts": "userPayout",
      "VL Fee": "vlFee",
      "Sold": "sold",
      "Avg Price": "price",
      "Pending": "pending",
      "Listed": "listed",
      "To Be Verified": "toBeVerified",
      "Verified": "verified",
      "Delisted": "delisted",
      "Unsold": "unsold",
      "Settlement": "profit",
    };

    const normalizeValue = (values: number[]) => {
      const base = values.find((value) => value > 0) ?? values[0] ?? 0;
      return values.map((value) => (base > 0 ? Number(((value / base) * 100).toFixed(1)) : 0));
    };

    const smoothSeries = (series: number[]) => {
      const hasPositive = series.some((value) => Number(value || 0) > 0);
      if (!hasPositive) {
        return series;
      }

      return series.map((rawValue, index) => {
        const value = Number(rawValue || 0);
        if (value > 0) {
          return value;
        }

        let prevIndex = index - 1;
        while (prevIndex >= 0 && Number(series[prevIndex] || 0) <= 0) {
          prevIndex -= 1;
        }

        let nextIndex = index + 1;
        while (nextIndex < series.length && Number(series[nextIndex] || 0) <= 0) {
          nextIndex += 1;
        }

        const prevValue = prevIndex >= 0 ? Number(series[prevIndex] || 0) : null;
        const nextValue = nextIndex < series.length ? Number(series[nextIndex] || 0) : null;

        if (prevValue && nextValue) {
          const gap = nextIndex - prevIndex;
          const ratio = gap > 0 ? (index - prevIndex) / gap : 0;
          return Number((prevValue + (nextValue - prevValue) * ratio).toFixed(2));
        }

        if (prevValue) {
          return prevValue;
        }

        if (nextValue) {
          return nextValue;
        }

        return value;
      });
    };

    const dateRows = chartData.map((item: TrendsChartDataPoint) => ({ date: item.date } as Record<string, string | number>));

    activeSelectedMetrics.forEach((metric) => {
      let values: number[] = [];

      if (baseSeries[metric]) {
        const key = baseSeries[metric];
        values = chartData.map((item: TrendsChartDataPoint) => Number(item[key] ?? 0));
      } else if (leagueSeries[metric]) {
        values = chartData.map((item: TrendsChartDataPoint) => Number(leagueSeries[metric][item.date] ?? 0));
      } else if (marketplaceSeries[metric]) {
        values = chartData.map((item: TrendsChartDataPoint) => Number(marketplaceSeries[metric][item.date] ?? 0));
      } else if (metric === "Total") {
        values = chartData.map((item: TrendsChartDataPoint) => Number(item.userPayout ?? 0));
      } else if (metric === "Self Pricing") {
        values = chartData.map((item: TrendsChartDataPoint) => Math.round(Number(item.userPayout ?? 0) * 0.65));
      } else if (metric === "Concierge") {
        values = chartData.map((item: TrendsChartDataPoint) => Math.round(Number(item.userPayout ?? 0) * 0.35));
      } else if (metric === "Total Users") {
        values = chartData.map(() => Number(organizationStats.totalUsers ?? 0));
      } else if (metric === "Active Users") {
        values = chartData.map(() => Number(organizationStats.activeUsers ?? 0));
      } else if (metric === "Avg Rev/User") {
        values = chartData.map(() => Number(organizationStats.avgRevenuePerUser ?? 0));
      } else if (metric === "Delist Rate") {
        values = chartData.map(() => Number(organizationStats.delistRate ?? 0));
      }

      const normalized = normalizeValue(smoothSeries(values));
      const rawKey = `raw_${metric}`;

      normalized.forEach((value, index) => {
        dateRows[index][metric] = value;
        dateRows[index][rawKey] = Number(values[index] ?? 0);
      });
    });

    return dateRows;
  }, [activeSelectedMetrics, chartData, leagueSeries, marketplaceSeries, organizationStats]);

  const handleRefresh = () => {
    void refetch().catch(() => {
      setToastMessage("Failed to refresh trends data");
      setShowToast(true);
    });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleCustomDateChange = (startDate: string, endDate: string) => {
    console.group('[TrendsPage] 📅 Custom Date Changed');
    console.log('Start:', startDate, 'End:', endDate);
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    setShowCustomDatePicker(false);
    console.log('Date picker closed, filtering triggered');
    console.groupEnd();
  };
  // Helper function to safely parse change values
  const getChangeValue = (value: string): number => {
    return parseFloat(value) || 0;
  };

  const toggleMetric = (metric: string) => {
    if (selectedMetrics.includes(metric)) {
      setSelectedMetrics(selectedMetrics.filter((m) => m !== metric));
    } else if (selectedMetrics.length < 6) {
      setSelectedMetrics([...selectedMetrics, metric]);
    } else {
      setToastMessage("Limit reached! You can only select 6 metrics maximum");
      setShowToast(true);
    }
  };

  const clearAllMetrics = () => {
    setSelectedCategory('All');
    setSelectedMetrics(["Total Revenue", "QA Profit", "Sold", "Avg Price"]);
  };

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Color mapping for different metric types
  const getMetricColor = (metric: string): string => {
    const colors: Record<string, string> = {
      "Total Revenue": "#4f8ef7",
      "QA Profit": "#16a34a",
      "User Payouts": "#f59e0b",
      "VL Fee": "#a855f7",
      "Pending": "#f97316",
      "Listed": "#3b82f6",
      "Sold": "#22c55e",
      "Settlement": "#f97316",
      "Avg Price": "#8b5cf6",
      "To Be Verified": "#f97316",
      "Verified": "#3b82f6",
      "Delisted": "#ef4444",
      "Unsold": "#6b7280",
      "Total": "#16a34a",
      "Self Pricing": "#3b82f6",
      "Concierge": "#6b7280",
      "Total Users": "#4f8ef7",
      "Active Users": "#16a34a",
      "Avg Rev/User": "#f59e0b",
      "Delist Rate": "#ef4444",
      "NBA": "#f97316",
      "NFL": "#3b82f6",
      "MLB": "#22c55e",
      "NHL": "#a855f7",
      "MLS": "#ec4899",
      "NCAA FB": "#6366f1",
      "NCAA BB": "#f59e0b",
      "Concerts": "#14b8a6",
      "Theater": "#8b5cf6",
      "StubHub": "#f97316",
      "SeatGeek": "#3b82f6",
      "Ticketmaster": "#22c55e",
      "Vivid Seats": "#a855f7",
      "TickPick": "#ec4899",
      "Gametime": "#6366f1"
    };
    return colors[metric] || "#64748b";
  };

  const getMetricSeries = (metric: string): number[] => {
    const directSeriesMap: Record<string, keyof TrendsChartDataPoint> = {
      "Total Revenue": "revenue",
      "QA Profit": "profit",
      "User Payouts": "userPayout",
      "VL Fee": "vlFee",
      "Pending": "pending",
      "Listed": "listed",
      "Sold": "sold",
      "Settlement": "profit",
      "Avg Price": "price",
      "To Be Verified": "toBeVerified",
      "Verified": "verified",
      "Delisted": "delisted",
      "Unsold": "unsold",
    };

    if (directSeriesMap[metric]) {
      const seriesKey = directSeriesMap[metric];
      return chartData.map((row: TrendsChartDataPoint) => Number(row[seriesKey] ?? 0));
    }

    if (leagueSeries[metric]) {
      return chartData.map((row: TrendsChartDataPoint) => Number(leagueSeries[metric][row.date] ?? 0));
    }

    if (marketplaceSeries[metric]) {
      return chartData.map((row: TrendsChartDataPoint) => Number(marketplaceSeries[metric][row.date] ?? 0));
    }

    if (metric === "Total") {
      return chartData.map((row: TrendsChartDataPoint) => Number(row.userPayout ?? 0));
    }

    if (metric === "Self Pricing") {
      return chartData.map((row: TrendsChartDataPoint) => Math.round(Number(row.userPayout ?? 0) * 0.65));
    }

    if (metric === "Concierge") {
      return chartData.map((row: TrendsChartDataPoint) => Math.round(Number(row.userPayout ?? 0) * 0.35));
    }

    if (metric === "Total Users") {
      return chartData.map(() => organizationStats.totalUsers);
    }

    if (metric === "Active Users") {
      return chartData.map(() => organizationStats.activeUsers);
    }

    if (metric === "Avg Rev/User") {
      return chartData.map(() => organizationStats.avgRevenuePerUser);
    }

    if (metric === "Delist Rate") {
      return chartData.map(() => organizationStats.delistRate);
    }

    return chartData.map(() => 0);
  };

  const getSmoothedSeries = (series: number[]): number[] => {
    if (!series.length) {
      return series;
    }

    const hasPositive = series.some((value) => Number(value || 0) > 0);
    if (!hasPositive) {
      return series;
    }

    return series.map((rawValue, index) => {
      const value = Number(rawValue || 0);
      if (value > 0) {
        return value;
      }

      let prevIndex = index - 1;
      while (prevIndex >= 0 && Number(series[prevIndex] || 0) <= 0) {
        prevIndex -= 1;
      }

      let nextIndex = index + 1;
      while (nextIndex < series.length && Number(series[nextIndex] || 0) <= 0) {
        nextIndex += 1;
      }

      const prevValue = prevIndex >= 0 ? Number(series[prevIndex] || 0) : null;
      const nextValue = nextIndex < series.length ? Number(series[nextIndex] || 0) : null;

      if (prevValue && nextValue) {
        const gap = nextIndex - prevIndex;
        const ratio = gap > 0 ? (index - prevIndex) / gap : 0;
        return Number((prevValue + (nextValue - prevValue) * ratio).toFixed(2));
      }

      if (prevValue) {
        return prevValue;
      }

      if (nextValue) {
        return nextValue;
      }

      return value;
    });
  };

  const getDisplayMetricSeries = (metric: string): number[] => {
    return getSmoothedSeries(getMetricSeries(metric));
  };

  const isCurrencyMetric = (metric: string) => {
    return [
      "Total Revenue",
      "QA Profit",
      "User Payouts",
      "VL Fee",
      "Avg Price",
      "Total",
      "Self Pricing",
      "Concierge",
      "Avg Rev/User",
      "NBA", "NFL", "MLB", "NHL", "MLS", "NCAA FB", "NCAA BB", "Concerts", "Theater",
      "StubHub", "SeatGeek", "Ticketmaster", "Vivid Seats", "TickPick", "Gametime",
    ].includes(metric);
  };

  const isPercentMetric = (metric: string) => metric === "Delist Rate";

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  const formatMetricValue = (metric: string, value: number) => {
    if (isPercentMetric(metric)) {
      return `${value.toFixed(1)}%`;
    }

    if (isCurrencyMetric(metric)) {
      return currencyFormatter.format(value);
    }

    if (Number.isInteger(value)) {
      return value.toLocaleString('en-US');
    }

    return value.toFixed(2);
  };

  const CombinedTrendTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const row = payload[0].payload as Record<string, any>;
    return (
      <div style={{
        backgroundColor: isDarkMode ? '#111827' : '#ffffff',
        border: isDarkMode ? '1px solid #374151' : '1px solid #dbe4f0',
        borderRadius: 12,
        padding: 12,
        color: isDarkMode ? '#fff' : '#111827',
        minWidth: 240,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
          {`Date: ${label}`}
        </div>
        {payload.map((entry) => {
          const metric = String(entry.name);
          const rawValue = Number(row[`raw_${metric}`] ?? 0);
          const formattedRaw = formatMetricValue(metric, rawValue);

          return (
            <div key={metric} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', fontSize: 12, marginBottom: 10, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, backgroundColor: entry.color ?? getMetricColor(metric), borderRadius: '50%' }} />
                <span>{metric}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 700 }}>{formattedRaw}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const sumChartRowsForMetric = (metric: string, rows: TrendsChartDataPoint[]): number => {
    if (metric === 'Avg Price') {
      const totalRevenue = rows.reduce((sum, row) => sum + Number(row.revenue ?? 0), 0);
      const totalSold = rows.reduce((sum, row) => sum + Number(row.sold ?? 0), 0);
      return totalSold > 0 ? Math.round(totalRevenue / totalSold) : 0;
    }

    if (metric === 'Total Users') {
      return Number(organizationStats.totalUsers ?? 0);
    }

    if (metric === 'Active Users') {
      return Number(organizationStats.activeUsers ?? 0);
    }

    if (metric === 'Avg Rev/User') {
      return Number(organizationStats.avgRevenuePerUser ?? 0);
    }

    if (metric === 'Delist Rate') {
      return Number(organizationStats.delistRate ?? 0);
    }

    const chartFieldByMetric: Partial<Record<string, keyof TrendsChartDataPoint>> = {
      'Total Revenue': 'revenue',
      'QA Profit': 'qaProfit',
      'User Payouts': 'userPayout',
      'VL Fee': 'vlFee',
      Pending: 'pending',
      Listed: 'listed',
      Sold: 'sold',
      Settlement: 'profit',
      'To Be Verified': 'toBeVerified',
      Verified: 'verified',
      Delisted: 'delisted',
      Unsold: 'unsold',
    };

    const chartField = chartFieldByMetric[metric];
    if (chartField) {
      return rows.reduce((sum, row) => sum + Number(row[chartField] ?? 0), 0);
    }

    if (metric === 'Total') {
      return rows.reduce((sum, row) => sum + Number(row.userPayout ?? 0), 0);
    }

    if (metric === 'Self Pricing') {
      return rows.reduce((sum, row) => sum + Math.round(Number(row.userPayout ?? 0) * 0.65), 0);
    }

    if (metric === 'Concierge') {
      return rows.reduce((sum, row) => sum + Math.round(Number(row.userPayout ?? 0) * 0.35), 0);
    }

    if (leagueSeries[metric]) {
      return rows.reduce((sum, row) => sum + Number(leagueSeries[metric][row.date] ?? 0), 0);
    }

    if (marketplaceSeries[metric]) {
      return rows.reduce((sum, row) => sum + Number(marketplaceSeries[metric][row.date] ?? 0), 0);
    }

    return 0;
  };

  const getMetricDisplayValue = (metric: string): number => {
    const dashboardAlignedPeriod = selectedDateRange === 'All' || selectedDateRange === 'YTD';
    const aggregateFinancialMetrics = new Set([
      'Total Revenue',
      'QA Profit',
      'Sold',
      'Avg Price',
      'User Payouts',
      'VL Fee',
    ]);

    if (revenueAggregate && dashboardAlignedPeriod && aggregateFinancialMetrics.has(metric)) {
      if (metric === 'Total Revenue') {
        return Number(revenueAggregate.overAllSales ?? 0);
      }
      if (metric === 'QA Profit') {
        return Number(revenueAggregate.quickasystFee ?? 0);
      }
      if (metric === 'Sold') {
        return Number(revenueAggregate.overAllTickets ?? 0);
      }
      if (metric === 'Avg Price') {
        const sales = Number(revenueAggregate.overAllSales ?? 0);
        const tickets = Number(revenueAggregate.overAllTickets ?? 0);
        if (tickets > 0) {
          return Math.round(sales / tickets);
        }
      }
      if (metric === 'User Payouts' || metric === 'VL Fee') {
        const sales = Number(revenueAggregate.overAllSales ?? 0);
        const qaProfit = Number(revenueAggregate.quickasystFee ?? 0);
        const vlFee = Math.round(sales * 0.08);
        if (metric === 'VL Fee') {
          return vlFee;
        }
        return Math.max(0, sales - qaProfit - vlFee);
      }
    }

    if (dashboardAlignedPeriod && aggregateFinancialMetrics.has(metric) && revenueLoading) {
      return 0;
    }

    return sumChartRowsForMetric(metric, chartData);
  };

  const getMetricPeriodChange = (metric: string): number => {
    if (chartData.length < 2) {
      return 0;
    }

    const windowSize = Math.max(1, Math.min(30, Math.floor(chartData.length / 2)));
    const recentRows = chartData.slice(-windowSize);
    const priorRows = chartData.slice(-windowSize * 2, -windowSize);

    const compareRows = priorRows.length > 0
      ? { first: priorRows, second: recentRows }
      : (() => {
          const midpoint = Math.floor(chartData.length / 2);
          return {
            first: chartData.slice(0, midpoint),
            second: chartData.slice(midpoint),
          };
        })();

    const firstValue = sumChartRowsForMetric(metric, compareRows.first);
    const secondValue = sumChartRowsForMetric(metric, compareRows.second);

    if (!firstValue) {
      return 0;
    }

    return Number((((secondValue - firstValue) / firstValue) * 100).toFixed(1));
  };

  const getMetricKpiHint = (metric: string): string | null => {
    const dashboardAlignedPeriod = selectedDateRange === 'All' || selectedDateRange === 'YTD';
    if (
      metric === 'Sold' &&
      dashboardAlignedPeriod &&
      allTimeSoldCount != null &&
      (selectedCategory === 'All' || selectedCategory === 'Financial' || selectedCategory === 'Ticket Stats')
    ) {
      return `All-time sold (Dashboard): ${allTimeSoldCount.toLocaleString('en-US')}`;
    }
    return null;
  };

  const getMetricSummary = (metric: string) => {
    const displayValue = getMetricDisplayValue(metric);
    const change = getMetricPeriodChange(metric);
    const displayLabel = getTrendsMetricLabel(metric, selectedDateRange, selectedCategory);

    return {
      metric,
      displayLabel,
      hint: getMetricKpiHint(metric),
      value: displayValue,
      formattedValue: formatMetricValue(metric, displayValue),
      change,
      changeLabel: `${change >= 0 ? '↑' : '↓'} ${Math.abs(change).toFixed(1)}%`,
      isPositive: change >= 0,
    };
  };

  const metricCards = useMemo(() => {
    return activeSelectedMetrics.slice(0, 4).map((metric) => getMetricSummary(metric));
  }, [activeSelectedMetrics, chartData, leagueData, marketplaceData, revenueAggregate, revenueLoading, organizationStats, selectedDateRange, selectedCategory, allTimeSoldCount]);

  return (
    <div className={`${styles.container} ${viewMode === "mobile" ? styles.mobileView : ""} ${isDarkMode ? 'dark-mode' : ''}`} >
        {/* Loading Indicator */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            fontWeight: '500'
          }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              border: '2px solid rgba(255,255,255,0.3)',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            Loading trends data...
          </div>
        )}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#ef4444',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          animation: 'slideIn 0.3s ease-in-out',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {toastMessage}
        </div>
      )}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      {/* Header */}
      <div className={styles.header} >
        <div>
          <p className={styles.subtitle} style={isDarkMode ? { color: '#9ca3af' } : {}}>Compare Metrics Over Time</p>
          <h1 className={styles.title} style={isDarkMode ? { color: '#fff' } : {}}>Trends Dashboard</h1>
        </div>
        <div className={styles.searchSection}>
          
            <div className={styles.iconGroup}>
          
                            <button 
                              className={styles.iconBtn} 
                              onClick={handleRefresh}
                              disabled={loading}
                              title={loading ? "Fetching trends data..." : "Refresh data"}
                              style={isDarkMode ? { 
                                backgroundColor: '#374151', 
                                color: '#fff',
                                opacity: loading ? 0.6 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer'
                              } : { 
                                opacity: loading ? 0.6 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <RefreshRoundedIcon style={{ 
                                animation: loading ? 'spin 1s linear infinite' : 'none',
                                transformOrigin: 'center'
                              }} />
                            </button>
             
              <button
                 className={styles.iconBtn} 
                onClick={() => setIsDarkMode(!isDarkMode)}
                style={isDarkMode ? { backgroundColor: '#374151', color: '#fff' } : {}}
              >
                {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
              </button>
            </div>
          </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent} style={isDarkMode ? { backgroundColor: '#1f2937' } : {}}>
        {!loading && chartData.length === 0 && (
          <div style={{
            marginBottom: '24px',
            padding: '16px 20px',
            borderRadius: '10px',
            border: isDarkMode ? '1px solid #374151' : '1px solid #dbe3f0',
            backgroundColor: isDarkMode ? '#111827' : '#f8fbff',
            color: isDarkMode ? '#e5e7eb' : '#334155'
          }}>
            No live trends data is available for the selected date range.
          </div>
        )}
        {/* Metrics Selector */}
        <div className={styles.metricsSection} style={isDarkMode ? { backgroundColor: '#111827', borderRadius: '8px', padding: '20px' } : {}}>
               <div className={styles.filterSection}>
          <span className={styles.filterLabel} style={isDarkMode ? { color: '#d1d5db' } : {}}>Date Range:</span>
          <div className={styles.buttonGroup}>
            {dateRanges.map((range) => (
              <div key={range} style={{ position: 'relative' }}>
                <button
                  className={`${styles.rangeBtn} ${selectedDateRange === range ? styles.activeRange : ""}`}
                  title={(() => {
                    const { startDate, endDate } = getDatesForRangeButton(range);
                    return getTrendsPeriodButtonTitle(range, startDate, endDate);
                  })()}
                  onClick={() => {
                    setSelectedDateRange(range);
                    if (range === 'Custom') {
                      setShowCustomDatePicker(true);
                    } else {
                      setShowCustomDatePicker(false);
                    }
                  }}
                  style={selectedDateRange === range 
                    ? (isDarkMode ? { backgroundColor: '#3b82f6', color: '#fff', border: 'none' } : {})
                    : (isDarkMode ? { backgroundColor: '#374151', color: '#9ca3af', border: '1px solid #4b5563' } : {})
                  }
                >
                  {range}
                </button>
              </div>
            ))}
          </div>
          <p
            style={{
              margin: '10px 0 0',
              fontSize: '13px',
              lineHeight: 1.45,
              color: isDarkMode ? '#9ca3af' : '#64748b',
            }}
          >
            {periodDescription}
          </p>

          {/* Custom Date Picker */}
          {selectedDateRange === 'Custom' && showCustomDatePicker && (
            <div style={{
              marginTop: '16px',
              padding: '20px',
              backgroundColor: isDarkMode ? '#111827' : '#f9fafb',
              border: isDarkMode ? '2px solid #374151' : '2px solid #e5e7eb',
              borderRadius: '12px',
              display: 'flex',
              gap: '20px',
              alignItems: 'flex-end',
              flexWrap: 'wrap',
              boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {/* Start Date */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: isDarkMode ? '#9ca3af' : '#6b7280'
                }}>
                  <Calendar size={14} />
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: isDarkMode ? '1.5px solid #4b5563' : '1.5px solid #d1d5db',
                    backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                    color: isDarkMode ? '#fff' : '#000',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    minWidth: '140px',
                    outlineOffset: '-1px'
                  }}
                />
              </div>

              {/* End Date */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: isDarkMode ? '#9ca3af' : '#6b7280'
                }}>
                  <Calendar size={14} />
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: isDarkMode ? '1.5px solid #4b5563' : '1.5px solid #d1d5db',
                    backgroundColor: isDarkMode ? '#1f2937' : '#fff',
                    color: isDarkMode ? '#fff' : '#000',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    minWidth: '140px',
                    outlineOffset: '-1px'
                  }}
                />
              </div>

              {/* Apply Filter Button */}
              <button
                onClick={() => handleCustomDateChange(customStartDate, customEndDate)}
                disabled={!customStartDate || !customEndDate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: (!customStartDate || !customEndDate) ? '#9ca3af' : '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: (!customStartDate || !customEndDate) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: (!customStartDate || !customEndDate) ? 'none' : '0 2px 6px rgba(16, 185, 129, 0.3)',
                  opacity: (!customStartDate || !customEndDate) ? 0.7 : 1,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px'
                }}
              >
                ✓ Apply Filter
              </button>

              {/* Clear/Cancel Button with X Icon */}
              <button
                onClick={() => {
                  setShowCustomDatePicker(false);
                  setSelectedDateRange('90D');
                }}
                style={{
                  padding: '10px 16px',
                  backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
                  color: isDarkMode ? '#f3f4f6' : '#1f2937',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: '600',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? '#4b5563' : '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isDarkMode ? '#374151' : '#e5e7eb';
                }}
              >
                <X size={16} strokeWidth={3} />
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Category Filter */}
        <div className={styles.filterSection}>
          <span className={styles.filterLabel} style={isDarkMode ? { color: '#d1d5db' } : {}}>Filter by Category:</span>
          <div className={styles.categoryGroup}>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`${styles.categoryBtn} ${selectedCategory === cat ? styles.activeCategory : ""}`}
                onClick={() => setSelectedCategory(cat)}
                style={selectedCategory === cat
                  ? (isDarkMode ? { backgroundColor: '#10b981', color: '#fff', border: 'none' } : {})
                  : (isDarkMode ? { backgroundColor: '#374151', color: '#9ca3af', border: '1px solid #4b5563' } : {})
                }
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
          <div className={styles.metricsHeader}>
            <div className={styles.metricsTitle}>
              <span style={isDarkMode ? { color: '#fff' } : {}}>Select Metrics to Compare</span>
              <span className={styles.metricsCount} style={isDarkMode ? { backgroundColor: '#374151', color: '#fff' } : {}}>{selectedMetrics.length}/6</span>
            </div>
            <button className={styles.clearBtn} onClick={clearAllMetrics} style={isDarkMode ? { backgroundColor: '#ef4444', color: '#fff', border: 'none' } : {}}>
              Clear All
            </button>
          </div>

          {/* Category-based Metrics */}
          <div className={styles.metricsList}>
            {visibleMetricSections.map((section) => (
              <div key={section.title} className={styles.metricCategory}>
                <h4 className={styles.categoryTitle} style={isDarkMode ? { color: '#fff' } : {}}>{section.title}</h4>
                <div className={styles.metricRow}>
                  {section.metrics.map((metric) => (
                    <button
                      key={metric}
                      className={`${styles.metricBtn} ${selectedMetrics.includes(metric) ? styles.activeMetric : ""}`}
                      onClick={() => toggleMetric(metric)}
                      style={selectedMetrics.includes(metric) ? {
                        backgroundColor: getMetricColor(metric),
                        borderColor: getMetricColor(metric),
                        color: 'white'
                      } : {
                        borderColor: '#e2e8f0',
                        color: '#475569'
                      }}
                    >
                      {metric}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* KPI Cards - Dynamic based on filtered data */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={isDarkMode ? { color: '#fff', margin: '0' } : { margin: '0' }}>Key Metrics Overview</h3>
            <div style={{
           
              gap: '20px',
              fontSize: '14px',
              color: isDarkMode ? '#9ca3af' : '#666'
            }}>
              <span>📊 Selected Metrics: <strong style={isDarkMode ? { color: '#fff' } : {}}>{activeSelectedMetrics.length}/6</strong></span>
              <span>📈 Data Points: <strong style={isDarkMode ? { color: '#fff' } : {}}>{chartData.length}</strong></span>
              <span>🏷️ Category: <strong style={isDarkMode ? { color: '#fff' } : {}}>{selectedCategory}</strong></span>
              <span>📅 Period: <strong style={isDarkMode ? { color: '#fff' } : {}} title={periodDescription}>{selectedDateRange}</strong></span>
            </div>
          </div>
        </div>
        <p style={{
          margin: '0 0 16px',
          fontSize: '13px',
          color: isDarkMode ? '#9ca3af' : '#64748b',
        }}>
          {periodDescription}
        </p>
        <div className={styles.kpiGrid}>
          {metricCards.map((card) => (
            <div
              key={card.metric}
              className={styles.kpiCard}
              style={isDarkMode ? { backgroundColor: '#111827', border: '1px solid #374151', ['--metric-accent' as string]: getMetricColor(card.metric) } : { ['--metric-accent' as string]: getMetricColor(card.metric) }}
            >
              <div className={styles.kpiContent}>
                <span className={styles.kpiLabel} style={{ color: getMetricColor(card.metric) }}>{card.displayLabel ?? card.metric}</span>
                <h2 className={styles.kpiValue} style={isDarkMode ? { color: getMetricColor(card.metric) } : { color: getMetricColor(card.metric) }}>
                  {card.formattedValue}
                </h2>
                {card.hint && (
                  <span style={{
                    display: 'block',
                    fontSize: '11px',
                    marginTop: '4px',
                    color: isDarkMode ? '#9ca3af' : '#64748b',
                  }}>
                    {card.hint}
                  </span>
                )}
                <span className={`${styles.kpiChange} ${card.isPositive ? styles.up : styles.down}`}>
                  {card.changeLabel}
                </span>
              </div>
            </div>
          ))}
        </div>
        {/* Individual Charts Grid - Category-filtered, dynamic per selected metric */}
        {activeSelectedMetrics.length > 0 && (
          <div className={styles.chartsGrid}>
            {activeSelectedMetrics.map((metric) => {
              const metricSeries = getDisplayMetricSeries(metric);
              const metricSummary = getMetricSummary(metric);
              const metricData = chartData.map((row: TrendsChartDataPoint, index: number) => ({
                date: row.date,
                value: Number(metricSeries[index] ?? 0),
              }));

              return (
                <div key={metric} className={styles.chartCard} style={isDarkMode ? { backgroundColor: '#111827', border: '1px solid #374151' } : {}}>
                  <div className={styles.chartHeader}>
                    <div className={styles.chartHeaderLeft}>
                      <span className={styles.chartIndicator} style={{ backgroundColor: getMetricColor(metric) }} />
                      <span className={styles.chartMetricName}>{metric}</span>
                    </div>
                    <div className={styles.chartHeaderRight}>
                      <div className={styles.chartMetricValue}>{metricSummary.formattedValue}</div>
                      <div className={`${styles.chartMetricChange} ${metricSummary.isPositive ? styles.chartMetricChangeUp : styles.chartMetricChangeDown}`}>
                        {metricSummary.changeLabel}
                      </div>
                    </div>
                  </div>
                  <div className={styles.chartBody}>
                    <ResponsiveContainer width="100%" height={180}>
                      {metric === "Total Revenue" ? (
                        <AreaChart data={metricData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDarkMode ? '#9ca3af' : '#64748b' }} axisLine={false} tickLine={false} />
                          <YAxis tickFormatter={(value) => formatMetricValue(metric, Number(value))} tick={{ fontSize: 10, fill: isDarkMode ? '#9ca3af' : '#64748b' }} axisLine={false} tickLine={false} />
                          <Tooltip formatter={(value) => formatMetricValue(metric, Number(value))} contentStyle={isDarkMode ? { backgroundColor: '#1f2937', border: '1px solid #374151', color: '#fff' } : { borderRadius: '12px', border: '1px solid #dbe4f0' }} />
                          <Area type="monotone" dataKey="value" stroke={getMetricColor(metric)} fill={getMetricColor(metric)} fillOpacity={0.18} strokeWidth={3} name={metric} />
                        </AreaChart>
                      ) : (
                        <AreaChart data={metricData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDarkMode ? '#9ca3af' : '#64748b' }} axisLine={false} tickLine={false} />
                          <YAxis tickFormatter={(value) => formatMetricValue(metric, Number(value))} tick={{ fontSize: 10, fill: isDarkMode ? '#9ca3af' : '#64748b' }} axisLine={false} tickLine={false} />
                          <Tooltip formatter={(value) => formatMetricValue(metric, Number(value))} contentStyle={isDarkMode ? { backgroundColor: '#1f2937', border: '1px solid #374151', color: '#fff' } : { borderRadius: '12px', border: '1px solid #dbe4f0' }} />
                          <Area type="monotone" dataKey="value" stroke={getMetricColor(metric)} fill={getMetricColor(metric)} fillOpacity={0.12} strokeWidth={3} name={metric} />
                          <Line type="monotone" dataKey="value" stroke={getMetricColor(metric)} strokeWidth={2.5} dot={false} />
                        </AreaChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Combined Trend Comparison - Always show but with selected metrics */}
        <div className={styles.combinedChart}>
          <div className={styles.chartHeader}>
            <div className={styles.chartHeaderLeft}>
              <span className={styles.chartIndicator} style={{ backgroundColor: '#fbbf24' }} />
              <span className={styles.chartMetricName}>Combined Trend Comparison (Normalized)</span>
            </div>
          </div>
          <div className={styles.chartBody}>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={combinedChartData} margin={{ top: 20, right: 12, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDarkMode ? '#9ca3af' : '#64748b' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(value) => `${value}%`} tick={{ fontSize: 10, fill: isDarkMode ? '#9ca3af' : '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip
                  content={<CombinedTrendTooltip />}
                  cursor={false}
                />
                <Legend wrapperStyle={{ paddingTop: '16px', fontSize: '12px', color: isDarkMode ? '#fff' : '#475569' }} />
                {activeSelectedMetrics.map((metric) => (
                  <Line
                    key={metric}
                    type="monotone"
                    dataKey={metric}
                    stroke={getMetricColor(metric)}
                    strokeWidth={2.75}
                    name={metric}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            <p style={{ margin: '10px 0 0', fontSize: '12px', color: isDarkMode ? '#9ca3af' : '#64748b', textAlign: 'center' }}>
              Values normalized to 100% at the first available point for each selected metric.
            </p>
          </div>
        </div>


      </div>
    </div>
  );
};

export default TrendsPage;